import { clsx } from "clsx";
import {
  IconAlertCircle,
  IconCircleCheck,
  IconDownload,
  IconEye,
  IconFileDescription,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import { Button } from "../button";
import { IconPill } from "../icon-pill";
import { Text } from "../typography";

export type FileUploadItemStatus = "idle" | "uploading" | "uploaded" | "failed";

export interface FileUploadItemProps {
  /** Document category shown as the primary title in the uploaded state (e.g. "Tax Card"). */
  title?: string;
  fileName: string;
  /** Progress as 0–1. Only used when status = "uploading". */
  progress?: number;
  /** File size in bytes. Only used when status = "uploaded". */
  fileSize?: number;
  status: FileUploadItemStatus;
  /** Error message shown when status = "failed". */
  errorMessage?: string;
  onDownload?: () => void;
  onPreview?: () => void;
  onRemove?: () => void;
  onRetry?: () => void;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadItem({
  title,
  fileName,
  progress = 0,
  fileSize,
  status,
  errorMessage,
  onDownload,
  onPreview,
  onRemove,
  onRetry,
  className,
}: FileUploadItemProps) {
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const percent = Math.round(clampedProgress * 100);
  const isIdle = status === "idle";
  const isUploading = status === "uploading";
  const isUploaded = status === "uploaded";
  const isFailed = status === "failed";

  return (
    <div
      className={clsx(
        "flex w-full items-center gap-3 rounded-2xl border bg-bgWhite",
        isUploading ? "p-6" : "px-6 py-3",
        isFailed ? "border-accentError" : "border-borderDefault",
        className,
      )}
    >
      {isUploaded ? (
        <IconCircleCheck
          size={48}

          className="shrink-0 text-accentGreen"
        />
      ) : isFailed ? (
        <IconAlertCircle
          size={48}

          className="shrink-0 text-accentError"
        />
      ) : (
        <IconPill size={48} tone="light">
          <IconFileDescription size={24} />
        </IconPill>
      )}

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        {isIdle && (
          <>
            <Text variant="bodyLg" tone="dark" className="truncate">
              {title ?? fileName}
            </Text>
            <Text variant="bodySm" tone="muted" className="truncate">
              {title ? fileName : "Ready to upload"}
            </Text>
            {fileSize != null ? (
              <Text variant="bodyXs" tone="muted">
                {formatBytes(fileSize)}
              </Text>
            ) : null}
          </>
        )}
        {isUploading && (
          <>
            <Text variant="bodyLg" tone="body" className="truncate">
              {fileName}
            </Text>
            <div className="flex w-full items-center gap-1.5">
              <div className="relative flex h-2 flex-1 overflow-hidden rounded-full bg-bgLight">
                <div
                  className="h-full bg-brandPrimary transition-[width]"
                  style={{ width: `${percent}%` }}
                />
                <div
                  className="h-full bg-borderDark"
                  style={{ width: `${100 - percent}%` }}
                />
              </div>
              <Text variant="bodySmBold" tone="body" inline>
                {percent}% complete
              </Text>
            </div>
          </>
        )}
        {isUploaded && (
          <>
            <Text variant="bodyLg" tone="dark" className="truncate">
              {title ?? fileName}
            </Text>
            <Text variant="bodySm" tone="muted" className="truncate">
              {title ? `${fileName} - Uploaded` : "Uploaded"}
            </Text>
            {fileSize != null ? (
              <Text variant="bodyXs" tone="muted">
                {formatBytes(fileSize)}
              </Text>
            ) : null}
          </>
        )}
        {isFailed && (
          <>
            <Text variant="bodyLg" tone="dark" className="truncate">
              {title ?? fileName}
            </Text>
            <Text variant="bodySm" tone="inherit" className="truncate !text-accentError">
              {errorMessage ?? "Upload failed."}
            </Text>
            {title ? (
              <Text variant="bodyXs" tone="muted" className="truncate">
                {fileName}
              </Text>
            ) : null}
          </>
        )}
      </div>

      {isIdle && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${fileName}`}
          className="inline-flex size-6 items-center justify-center text-textBody hover:text-accentError"
        >
          <IconTrash size={20} />
        </button>
      )}

      {isUploaded && (
        <div className="flex items-center gap-6">
          {onPreview ? (
            <button
              type="button"
              onClick={onPreview}
              aria-label={`Preview ${fileName}`}
              className="inline-flex size-6 items-center justify-center text-textBody hover:text-textDark"
            >
              <IconEye size={20} />
            </button>
          ) : null}
          {onDownload ? (
            <button
              type="button"
              onClick={onDownload}
              aria-label={`Download ${fileName}`}
              className="inline-flex size-6 items-center justify-center text-textBody hover:text-textDark"
            >
              <IconDownload size={20} />
            </button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${fileName}`}
              className="inline-flex size-6 items-center justify-center text-textBody hover:text-accentError"
            >
              <IconTrash size={20} />
            </button>
          ) : null}
        </div>
      )}

      {isFailed && (
        <div className="flex items-center gap-3">
          {onRetry ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconRefresh size={16} />}
              onClick={onRetry}
            >
              Retry
            </Button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${fileName}`}
              className="inline-flex size-6 items-center justify-center text-textBody hover:text-accentError"
            >
              <IconTrash size={20} />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
